import { escapeHTML } from '../../utils/escape.js';
import { formatFechaHora } from '../../utils/formatters.js';
import { ANOMALY_TYPES } from '../../shared/timesheet-policy.js';
import { summarizeFichadas, minutosTrabajados } from '../../shared/fichadas-ui-policy.js';
import { FichadaEditDialog } from './FichadaEditDialog.js';

/**
 * Vista de Fichadas del panel de staff (Control de Horarios y Liquidación de
 * Sueldos, slice 3 — Auditoría). Función de render pura: `js/admin.js`
 * (wiring) le pasa `{ anomalies, empleados }` (de
 * `admin-fichadas.service.js#loadFichadas` + `admin-empleados.service.js#loadEmpleados`)
 * + un ui-state (loading/error/ready, diálogo de edición/cierre abierto).
 *
 * Muestra la lista de anomalías (Requirement: Anomaly Detection — abiertas hace
 * más de 12 h, >16h, invertidas) y un alta manual de fichada
 * (Requirement: Admin Fichada Create, Edit, and Close).
 *
 * Owner request (2026-09-02, "ver TODAS las fichadas de un empleado"):
 * también muestra un filtro por empleado + un listado completo de fichadas
 * (no solo anomalías) con una línea de resumen (cantidad, jornadas abiertas,
 * tiempo trabajado). El filtrado por empleado es SIEMPRE client-side sobre
 * `viewModel.fichadas` — `js/admin.js` decide QUÉ conjunto llega ahí (el
 * rango por defecto ya cargado, o el historial completo de un empleado
 * cuando el checkbox "Todo el historial" está tildado), esta vista solo lo
 * filtra por `uiState.filtro.empleadoId` si hace falta (no-op cuando el
 * conjunto ya viene acotado a un solo empleado).
 */

const FIELD =
    'w-full bg-base border border-border-subtle rounded-2xl px-4 py-3 text-sm text-text-primary focus:outline-none focus:border-brand';
const LABEL = 'block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-2';

/** Etiqueta legible por tipo de anomalía — cada una debe leerse distinta de las otras. */
const ANOMALY_LABELS = {
    [ANOMALY_TYPES.ABIERTA]: 'Jornada abierta hace más de 12 h',
    [ANOMALY_TYPES.DURACION_EXCESIVA]: 'Turno de más de 16 horas',
    [ANOMALY_TYPES.INVERTIDA]: 'Horario de salida anterior a la entrada',
};

/** Clases de color por tipo — refuerzo visual, no el único diferenciador (la etiqueta ya lo es). */
const ANOMALY_BADGE_CLASSES = {
    [ANOMALY_TYPES.ABIERTA]: 'bg-amber-50 border-amber-300 text-amber-900',
    [ANOMALY_TYPES.DURACION_EXCESIVA]: 'bg-red-50 border-red-300 text-red-900',
    [ANOMALY_TYPES.INVERTIDA]: 'bg-red-50 border-red-300 text-red-900',
};

/**
 * Formatea minutos EXACTOS (posiblemente fraccionarios — ver
 * `splitMinutesByDate` en timesheet-policy.js, que redondea a propósito CERO
 * veces) como "Hh MMm" para mostrar. El redondeo es SOLO de presentación:
 * este valor nunca se vuelve a mandar a ningún servicio, el motor de
 * liquidación sigue leyendo el minuto exacto por su cuenta.
 * @param {number|null|undefined} minutos
 * @returns {string}
 */
function formatMinutos(minutos) {
    if (minutos == null || Number.isNaN(minutos)) return '—';
    const totalRedondeado = Math.round(minutos);
    const horas = Math.floor(totalRedondeado / 60);
    const mins = totalRedondeado % 60;
    return `${horas}h ${String(mins).padStart(2, '0')}m`;
}

/**
 * @param {{ fichadaId: string, tipo: string, empleadoNombre: string, entrada_at: string|null, salida_at: string|null, minutosTotal: number }} anomaly
 * @returns {string}
 */
function renderAnomalyRow({ fichadaId, tipo, empleadoNombre, entrada_at, salida_at, minutosTotal }) {
    const safeId = escapeHTML(fichadaId);
    const label = ANOMALY_LABELS[tipo] ?? tipo;
    const badgeClass = ANOMALY_BADGE_CLASSES[tipo] ?? 'bg-surface border-border-subtle text-text-muted';
    // Abierta -> se corrige cerrando la jornada; duración/invertida -> se
    // corrige editando los horarios (ninguna de las dos tiene un `salida_at`
    // confiable que simplemente "cerrar" pueda arreglar).
    const accion = tipo === ANOMALY_TYPES.ABIERTA ? 'cerrar' : 'editar';
    const actionLabel = accion === 'cerrar' ? 'Cerrar jornada' : 'Corregir horarios';
    const detalle =
        tipo === ANOMALY_TYPES.DURACION_EXCESIVA
            ? `Duración: ${formatMinutos(minutosTotal)}`
            : tipo === ANOMALY_TYPES.ABIERTA
              ? 'Sin hora de salida'
              : 'Salida registrada antes que la entrada';

    return `
        <div class="flex items-center justify-between gap-4 py-4 border-b border-border-subtle last:border-b-0" data-tipo="${escapeHTML(tipo)}">
            <div class="flex items-center gap-4 min-w-0">
                <span class="shrink-0 text-[10px] font-black uppercase tracking-widest border rounded-full px-3 py-1 ${badgeClass}">${escapeHTML(label)}</span>
                <div class="min-w-0">
                    <p class="font-bold text-text-primary truncate">${escapeHTML(empleadoNombre || 'Empleado')}</p>
                    <p class="text-xs text-text-muted mt-1 truncate">
                        ${formatFechaHora(entrada_at)} → ${salida_at ? formatFechaHora(salida_at) : 'sin cierre'} · ${escapeHTML(detalle)}
                    </p>
                </div>
            </div>
            <button data-action="fichada-corregir" data-accion="${accion}" data-id="${safeId}" class="shrink-0 px-4 py-2 rounded-xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors">${actionLabel}</button>
        </div>
    `;
}

/**
 * Opciones del `<select>` de filtro por empleado: "Todos los empleados"
 * (valor `''`, default) primero, luego los ACTIVOS, luego los INACTIVOS
 * marcados "(inactivo)" — a diferencia del `<select>` de alta manual
 * (`renderCrearForm`), que solo lista activos: acá el admin puede querer
 * ver el historial de alguien que ya no trabaja más.
 * @param {Array<{ id: string, nombre: string, activo: boolean }>} empleados
 * @param {string} empleadoIdSeleccionado
 * @returns {string}
 */
function renderFiltroEmpleadoOptions(empleados, empleadoIdSeleccionado) {
    const activos = (empleados ?? []).filter((empleado) => empleado.activo);
    const inactivos = (empleados ?? []).filter((empleado) => !empleado.activo);
    const selected = (id) => (id === empleadoIdSeleccionado ? ' selected' : '');

    const optionActivo = (empleado) =>
        `<option value="${escapeHTML(empleado.id)}"${selected(empleado.id)}>${escapeHTML(empleado.nombre)}</option>`;
    const optionInactivo = (empleado) =>
        `<option value="${escapeHTML(empleado.id)}"${selected(empleado.id)}>${escapeHTML(empleado.nombre)} (inactivo)</option>`;

    return [
        `<option value=""${selected('')}>Todos los empleados</option>`,
        ...activos.map(optionActivo),
        ...inactivos.map(optionInactivo),
    ].join('');
}

/**
 * Filtro por empleado + "Todo el historial" (owner request 2026-09-02).
 * El checkbox se OCULTA (`hidden`, no una clase CSS — ver Assertion Quality
 * Rules) mientras "Todos los empleados" está seleccionado: "todo el
 * historial" no tiene sentido sin un empleado puntual. `js/admin.js` es
 * quien de verdad dispara la query `sinRango` al tildarlo; esta vista solo
 * refleja el estado (`uiState.filtro`), nunca decide sola cuándo pedir más
 * datos — mismo split que el resto del archivo (vista pura, wiring en
 * admin.js).
 * @param {Array<object>} empleados
 * @param {{ empleadoId: string, todoHistorial: boolean, desde?: string, hasta?: string, error?: string|null }} filtro
 * @returns {string}
 */
function renderFiltroForm(empleados, filtro) {
    const empleadoId = filtro?.empleadoId ?? '';
    const todoHistorial = Boolean(filtro?.todoHistorial);
    const todosSeleccionado = empleadoId === '';
    // L7: "Desde"/"Hasta" (fechas ART) acotan lista, resumen y anomalías.
    // Con "Todo el historial" de un empleado no aplican: quedan deshabilitadas.
    const fechasDeshabilitadas = todoHistorial && !todosSeleccionado ? 'disabled' : '';
    const errorHtml = filtro?.error
        ? `<p role="alert" class="basis-full text-xs font-bold text-red-700">${escapeHTML(filtro.error)}</p>`
        : '';

    return `
        <form data-action-form="fichada-filtro" class="flex flex-wrap items-end gap-4 mb-4">
            <div>
                <label class="${LABEL}">Empleado</label>
                <select name="empleado_id" class="${FIELD}">
                    ${renderFiltroEmpleadoOptions(empleados, empleadoId)}
                </select>
            </div>
            <div>
                <label class="${LABEL}" for="fichada-filtro-desde">Desde</label>
                <input id="fichada-filtro-desde" type="date" name="desde" value="${escapeHTML(filtro?.desde ?? '')}" ${fechasDeshabilitadas} class="${FIELD}" />
            </div>
            <div>
                <label class="${LABEL}" for="fichada-filtro-hasta">Hasta</label>
                <input id="fichada-filtro-hasta" type="date" name="hasta" value="${escapeHTML(filtro?.hasta ?? '')}" ${fechasDeshabilitadas} class="${FIELD}" />
            </div>
            <div ${todosSeleccionado ? 'hidden' : ''}>
                <label class="flex items-center gap-2 text-xs font-bold text-text-muted pb-3">
                    <input type="checkbox" name="todo_historial" ${todoHistorial ? 'checked' : ''} class="accent-brand" />
                    Todo el historial
                </label>
            </div>
            ${errorHtml}
        </form>
    `;
}

/**
 * Línea de resumen del conjunto de fichadas mostrado (owner request
 * 2026-09-02): cantidad, jornadas abiertas, tiempo total trabajado.
 * Agregación pura vía `summarizeFichadas` (js/shared/fichadas-ui-policy.js)
 * — las `anulada` ya vienen excluidas de ahí, esta función solo formatea.
 * @param {{ total: number, abiertas: number, minutosTotal: number }} resumen
 * @returns {string}
 */
function renderResumenLinea({ total, abiertas, minutosTotal }) {
    const fichadaLabel = total === 1 ? 'fichada' : 'fichadas';
    const abiertaLabel = abiertas === 1 ? 'abierta' : 'abiertas';
    return `<p class="text-xs text-text-muted mb-4">${total} ${fichadaLabel} · ${abiertas} ${abiertaLabel} · ${formatMinutos(minutosTotal)} trabajadas</p>`;
}

/**
 * Aviso neutral cuando "Todo el historial" pegó contra el tope de 2000 filas
 * (review-6d2bc4aaef1ed2ce finding 2 — antes el recorte era silencioso: un
 * empleado con más de 2000 fichadas veía "todo su historial" truncado sin
 * ningún indicio de que faltaban filas). `js/admin.js` decide cuándo
 * `viewModel.truncado` llega en `true` (viene de
 * `admin-fichadas.service.js#loadFichadas`, que a su vez lo recibe de
 * `fichadas.repository.js#listFichadasHistorial`); esta función solo
 * renderiza el aviso, no decide nada.
 * @returns {string}
 */
function renderTruncadoAviso() {
    // TODO(review-7ff0f203512701ce, readability): tie this number to the
    // repository cap (LIST_FICHADAS_SIN_RANGO_LIMIT) via the service result
    // in a follow-up; kept literal here to stay inside the frozen review scope.
    return '<p class="text-xs text-text-muted mb-4" role="status">Se muestran las últimas 2000 fichadas. Para ver más antiguas, usá un rango de fechas.</p>';
}

/**
 * Una fila del listado completo de fichadas (a diferencia de
 * `renderAnomalyRow`, que solo lista lo que necesita corrección: esto
 * muestra CUALQUIER fichada del conjunto actual, corregida o no).
 *
 * Una fichada `anulada` se sigue mostrando (nunca se borra del listado —
 * mismo criterio AD13 que el historial de Liquidación), marcada con una
 * badge "Anulada" y atenuada, pero excluida de `renderResumenLinea` arriba
 * (calculado sobre el conjunto YA filtrado por `summarizeFichadas`, no acá).
 * @param {object} fichada fila cruda de `fichadas.repository.js` (incluye el embed `empleados(nombre)`)
 * @returns {string}
 */
function renderFichadaFila(fichada) {
    const nombre = escapeHTML(fichada.empleados?.nombre ?? 'Empleado');
    const abierta = fichada.salida_at == null;
    const duracion = abierta ? 'En curso' : formatMinutos(minutosTrabajados(fichada));
    const anuladaBadge = fichada.anulada
        ? '<span class="ml-2 text-[10px] font-black uppercase tracking-widest text-text-muted border border-border-subtle rounded-full px-2 py-0.5">Anulada</span>'
        : '';
    // Anulada: tachada (mismo criterio visual que pagos/adelantos anulados).
    const tachado = fichada.anulada ? ' line-through' : '';

    return `
        <div data-fichada-id="${escapeHTML(fichada.id)}" class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3 border-b border-border-subtle last:border-b-0${fichada.anulada ? ' opacity-60' : ''}">
            <div class="min-w-0">
                <p class="font-bold text-text-primary truncate">${nombre}${anuladaBadge}</p>
                <p class="text-xs text-text-muted mt-1 truncate${tachado}">${formatFechaHora(fichada.entrada_at)} → ${fichada.salida_at ? formatFechaHora(fichada.salida_at) : 'sin cierre'}</p>
            </div>
            <div class="flex items-center gap-2 shrink-0">
                <span class="text-xs font-bold text-text-muted${tachado}">${escapeHTML(duracion)}</span>
                ${renderAccionesFila(fichada)}
            </div>
        </div>
    `;
}

const FILA_ACCION_BTN =
    'px-3 py-1.5 rounded-xl border border-border-subtle text-[10px] font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors';

/**
 * Acciones de una fila del listado (F-C/F-D, owner decisions 2026-09-27):
 * CUALQUIER fichada no anulada se puede corregir ("Editar") o anular
 * ("Anular"), y una abierta — sea o no anomalía, incluida un alta manual de
 * hoy — se puede cerrar ("Cerrar jornada"). Todas abren el MISMO diálogo
 * (`FichadaEditDialog`) y pasan por el mismo servicio/RPC
 * (`admin_edit_fichada`) que las correcciones de anomalías. Una anulada no
 * ofrece nada: la RPC la rechaza (`fichada_anulada`) y la fila queda solo
 * como rastro.
 * @param {{ id: string, salida_at?: string|null, anulada?: boolean }} fichada
 * @returns {string}
 */
function renderAccionesFila(fichada) {
    if (fichada.anulada) return '';
    const id = escapeHTML(fichada.id);
    const boton = (accion, label) =>
        `<button type="button" data-action="fichada-fila" data-accion="${accion}" data-id="${id}" class="${FILA_ACCION_BTN}">${label}</button>`;
    return [
        fichada.salida_at == null ? boton('cerrar', 'Cerrar jornada') : '',
        boton('editar', 'Editar'),
        boton('anular', 'Anular'),
    ].join('');
}

/**
 * Listado completo de fichadas del conjunto actual (ya filtrado por
 * empleado si corresponde). Vacío !== error: puede ser legítimamente cero
 * si el empleado elegido no tiene fichadas en el conjunto cargado.
 * @param {Array<object>} fichadas
 * @returns {string}
 */
function renderListaFichadas(fichadas) {
    if (!fichadas || fichadas.length === 0) {
        return '<p class="text-xs text-text-muted py-4">No hay fichadas para mostrar.</p>';
    }
    return fichadas.map(renderFichadaFila).join('');
}

/**
 * Alta manual de una fichada (Req: Admin Fichada Create, Edit, and Close).
 * Solo lista empleados ACTIVOS: uno inactivo no debería sumar horas nuevas.
 * @param {Array<{ id: string, nombre: string, activo: boolean }>} empleados
 * @returns {string}
 */
function renderCrearForm(empleados) {
    const options = (empleados ?? [])
        .filter((empleado) => empleado.activo)
        .map((empleado) => `<option value="${escapeHTML(empleado.id)}">${escapeHTML(empleado.nombre)}</option>`)
        .join('');

    return `
        <form data-action-form="fichada-crear" class="bg-surface border border-border-subtle rounded-2xl p-6">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div>
                    <label class="${LABEL}">Empleado</label>
                    <select name="empleado_id" required class="${FIELD}">
                        <option value="">Elegir...</option>
                        ${options}
                    </select>
                </div>
                <div>
                    <label class="${LABEL}">Entrada</label>
                    <input type="datetime-local" name="entrada_at" required class="${FIELD}" />
                </div>
                <div class="sm:col-span-2">
                    <label class="${LABEL}">Motivo</label>
                    <textarea name="motivo" required rows="2" class="${FIELD}"></textarea>
                </div>
            </div>
            <button type="submit" data-action="fichada-crear-guardar" class="px-8 py-3 bg-brand text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg active:scale-95 transition-transform disabled:opacity-60 disabled:pointer-events-none">
                Crear fichada
            </button>
        </form>
    `;
}

/**
 * @param {{ anomalies: Array<object>, empleados: Array<object>, fichadas?: Array<object>, truncado?: boolean }|null} viewModel usado solo en `status: 'ready'`. `truncado` (review-6d2bc4aaef1ed2ce finding 2) muestra el aviso de tope de "Todo el historial" cuando viene en `true`.
 * @param {{ status: 'loading'|'error'|'ready', dialog?: object|null, filtro?: { empleadoId: string, todoHistorial: boolean } }} [uiState]
 * @returns {string} HTML de la vista completa (filtro + resumen + listado + anomalías + alta manual + diálogo).
 */
export function FichadasView(viewModel, uiState = {}) {
    if (uiState.status === 'loading') {
        return `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="status" aria-live="polite">
                <i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand mb-4" aria-hidden="true"></i>
                <p class="text-xs font-black text-text-muted uppercase tracking-widest">Cargando fichadas...</p>
            </div>
        `;
    }

    if (uiState.status === 'error') {
        return `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="alert">
                <p class="text-sm font-bold text-text-primary mb-6">No pudimos cargar las fichadas. Probá de nuevo.</p>
                <button data-action="fichada-retry" class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">Reintentar</button>
            </div>
        `;
    }

    const anomalies = viewModel?.anomalies ?? [];
    const empleados = viewModel?.empleados ?? [];
    const anomalyListHtml =
        anomalies.length > 0
            ? anomalies.map(renderAnomalyRow).join('')
            : '<p class="text-xs text-text-muted py-4">No hay anomalías en el rango seleccionado.</p>';

    const filtro = uiState.filtro ?? { empleadoId: '', todoHistorial: false };
    const fichadasCargadas = viewModel?.fichadas ?? [];
    const fichadasFiltradas = filtro.empleadoId
        ? fichadasCargadas.filter((fichada) => fichada.empleado_id === filtro.empleadoId)
        : fichadasCargadas;
    const resumen = summarizeFichadas(fichadasFiltradas);
    const truncado = Boolean(viewModel?.truncado);

    return `
        <section aria-label="Fichadas">
            <h2 class="text-lg font-black uppercase tracking-tight text-text-primary mb-5">Fichadas</h2>

            ${renderFiltroForm(empleados, filtro)}
            ${renderResumenLinea(resumen)}
            ${truncado ? renderTruncadoAviso() : ''}
            <div data-role="fichadas-lista" class="bg-surface border border-border-subtle rounded-2xl px-5 mb-8">${renderListaFichadas(fichadasFiltradas)}</div>

            <span class="block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-2">Anomalías</span>
            <div class="bg-surface border border-border-subtle rounded-2xl px-5 mb-8">${anomalyListHtml}</div>

            <span class="block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-2">Alta manual</span>
            ${renderCrearForm(empleados)}

            ${uiState.dialog ? FichadaEditDialog(uiState.dialog) : ''}
        </section>
    `;
}
